import React, { useEffect, useState, useRef, memo, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  ChartBarIcon,
  InformationCircleIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import { Droplet, Thermometer, Gauge } from 'lucide-react';
import { format } from 'date-fns';
import { useWebSocket } from '../contexts/WebSocketContext';
import { tempUnit } from '../utils/temperatureUnit';
import SystemControls, { useDryerControls, MODE_COLOR, MODE_NAME } from './SystemControls';
import { getModeIcon } from './ModeIcons';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

// The setpoint block on a card. It is a tap target on the phone, so it is one
// here too. When the dryer is in a mode the value does not apply to, the phone
// dims it and refuses the tap rather than hiding it, and so does this.
const SetpointTile = ({ value, disabled, hint, onClick }) => {
  const body = (
    <>
      <div className="text-xs text-white/80 font-medium mb-1">SETPOINT</div>
      <div className="text-2xl font-bold text-white">
        {value !== null && value !== undefined && !isNaN(value) ? parseFloat(value).toFixed(2) : value}
      </div>
    </>
  );

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={disabled ? hint : 'Change setpoint'}
      className={`text-left rounded-lg px-4 py-3 border transition-all ${
        disabled
          ? 'bg-white/10 border-white/15 opacity-60 cursor-not-allowed'
          : 'bg-gradient-to-br from-white/25 to-white/10 backdrop-blur-sm border-white/20 hover:from-white/35 hover:to-white/20 hover:border-white/40'
      }`}
    >
      {body}
    </button>
  );
};

// The control mode, in the same three colours and with the same symbol the
// panel and the phone draw, and clickable the way the phone's is.
const ModeBadge = ({ mode, disabled, hint, onClick }) => {
  const name = MODE_NAME[mode] || 'Unknown';
  const color = MODE_COLOR[mode] || '#757575';
  const icon = getModeIcon(name, 18);

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={disabled ? hint : 'Change control mode'}
      className={`flex items-center gap-2 px-3.5 py-2 rounded-lg text-sm font-semibold text-white transition-all ${
        disabled ? 'opacity-60 cursor-not-allowed' : 'hover:-translate-y-px hover:brightness-110'
      }`}
      style={{ background: color, boxShadow: `0 4px 12px ${color}4d` }}
    >
      {icon}
      {name}
    </button>
  );
};

const MetricCard = ({ title, value, unit, icon: Icon, bgColor, subValue, subLabel, subUnit, subIcon: SubIcon, setpoint, modeControl, decimals = 1 }) => {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
      className="relative overflow-hidden rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-1"
      style={{ 
        background: bgColor,
        boxShadow: `0 4px 12px ${bgColor}40`
      }}
    >
      {/* Top shimmer effect */}
      <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-white/50 to-transparent opacity-80" />
      
      {/* Card Glow Effect */}
      <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent opacity-0 hover:opacity-50 transition-opacity pointer-events-none" />
      
      {/* Header */}
      <div className="relative flex items-center justify-between px-5 py-4 bg-gradient-to-b from-white/10 to-white/5 border-b border-white/15">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-white/20 flex items-center justify-center transition-transform hover:scale-105">
            <Icon className="w-5 h-5 text-white" />
          </div>
          <h3 className="text-white font-semibold tracking-wide">{title}</h3>
        </div>
        {modeControl && <ModeBadge {...modeControl} />}
      </div>
      
      {/* Body */}
      <div className="relative px-5 py-4">
        <div className="flex justify-between items-start">
          {/* Left side - Main Value */}
          <div>
            <div className="flex items-baseline gap-1">
              <span className="text-5xl font-bold text-white">
                {value !== null && value !== undefined && !isNaN(value) ? parseFloat(value).toFixed(decimals) : '--'}
              </span>
              {unit && <span className="text-2xl font-semibold text-white/80">{unit}</span>}
            </div>

            {/* Temperature below the main value */}
            {subValue !== undefined && subValue !== null && (
              <div className="mt-3 flex items-center gap-2">
                {SubIcon && <SubIcon className="w-4 h-4 text-white/70" />}
                <span className="text-sm text-white/80">
                  {subLabel}:
                </span>
                <span className="text-base font-semibold text-white">
                  {!isNaN(subValue) ? parseFloat(subValue).toFixed(1) : subValue}{subUnit}
                </span>
              </div>
            )}
          </div>
          
          {/* Right side - Setpoint */}
          <div className="flex flex-col gap-2 items-end">
            {setpoint && setpoint.value !== undefined && setpoint.value !== null && (
              <SetpointTile {...setpoint} />
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// Chart series colours, and the legend placement, both taken from the phone:
// no heading above either chart, legend along the top right.
const CHART_INLET = '#5cb85c';
const CHART_OUTLET = '#428bca';
const CHART_SETPOINT = '#d9534f';
const CHART_RATE = '#f0ad4e';

const legendProps = {
  verticalAlign: 'top',
  align: 'right',
  height: 32,
  iconType: 'plainline',
  wrapperStyle: { fontSize: 13 },
};

const tooltipProps = {
  contentStyle: {
    backgroundColor: 'white',
    border: '1px solid #E5E7EB',
    borderRadius: '8px',
  },
  formatter: (value) => `${parseFloat(value).toFixed(2)}`,
};

// Memoized Chart Components to prevent re-renders
const MoistureChart = memo(({ data }) => (
  <ResponsiveContainer width="100%" height={320}>
    <LineChart data={data}>
      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
      <XAxis dataKey="time" stroke="#6B7280" fontSize={12} />
      {/* Moisture is a percentage, so the axis says so and holds the phone's
          0-50 frame rather than rescaling under the reading. */}
      <YAxis
        domain={[0, 50]}
        ticks={[0, 10, 20, 30, 40, 50]}
        tickFormatter={(value) => `${value}%`}
        stroke="#6B7280"
        fontSize={12}
      />
      <Tooltip {...tooltipProps} />
      <Legend {...legendProps} />
      <Line
        type="monotone"
        dataKey="target"
        stroke={CHART_SETPOINT}
        strokeWidth={2}
        strokeDasharray="8 4"
        dot={false}
        name="Setpoint"
      />
      <Line
        type="monotone"
        dataKey="inlet"
        stroke={CHART_INLET}
        strokeWidth={3}
        dot={false}
        name="Inlet"
      />
      <Line
        type="monotone"
        dataKey="outlet"
        stroke={CHART_OUTLET}
        strokeWidth={3}
        dot={false}
        name="Outlet"
      />
    </LineChart>
  </ResponsiveContainer>
));

const DischargeRateChart = memo(({ data }) => (
  <ResponsiveContainer width="100%" height={300}>
    <LineChart data={data}>
      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
      <XAxis dataKey="time" stroke="#6B7280" fontSize={12} />
      {/* Bushels-per-hour dryers run 0-10000, so the axis grows to fit the
          data, while percent-style rates keep the familiar 0-110 frame. */}
      <YAxis
        domain={[0, (dataMax) => Math.max(110, dataMax)]}
        stroke="#6B7280"
        fontSize={12}
      />
      <Tooltip {...tooltipProps} />
      <Legend {...legendProps} />
      <Line
        type="monotone"
        dataKey="rate"
        stroke={CHART_RATE}
        strokeWidth={3}
        dot={false}
        name="Discharge Rate"
      />
    </LineChart>
  </ResponsiveContainer>
));

const HomeDataDisplay = ({ deviceSerial, onClose, readOnly = false }) => {
  const { getTopicData, subscribe, unsubscribe, isSubscribed, sendUpdate } = useWebSocket();
  const [, setChartData] = useState([]);
  const [liveData, setLiveData] = useState(null);
  const [product, setProduct] = useState(null);
  const [productIcon, setProductIcon] = useState(null);
  const [activeBin, setActiveBin] = useState(null);
  const [deviceInfo, setDeviceInfo] = useState(null);
  const lastChartUpdate = useRef(null);
  const chartDataRef = useRef([]);
  
  // Handle parameter updates via WebSocket
  const handleParameterUpdate = useCallback(async (parameter, value) => {
    if (!deviceSerial) return;
    
    try {
      // Awaited so a refusal reaches the dialog that asked for it rather than
      // becoming an unhandled rejection. The new value itself comes back on
      // the stream, so there is no local state to set here.
      await sendUpdate(deviceSerial, 'home', [{
        path: parameter,
        value: value
      }]);
    } catch (error) {
      console.error('Failed to update parameter:', error);
      throw error; // Re-throw so the dialog can show what went wrong
    }
  }, [deviceSerial, sendUpdate]);
  
  // Handle mode change
  const handleModeChange = useCallback(async (mode) => {
    if (!deviceSerial) return;

    try {
      // Send mode update via WebSocket and wait for ACK
      // The mode parameter contains the mode type (local_mode, manual_mode, automatic_mode)
      // We send it with path "mode" and the mode type as the value
      await sendUpdate(deviceSerial, 'home', [{
        path: 'mode',
        value: mode
      }]);
    } catch (error) {
      console.error('Failed to change mode:', error);
      throw error;
    }
  }, [deviceSerial, sendUpdate]);
  
  // isSubscribed is rebuilt every time anything subscribes, so naming it as a
  // dependency would tear this effect down and set it up again on its own
  // result. The socket helpers are read through a ref instead, and the effect
  // runs once per device.
  const ws = useRef(null);
  ws.current = { subscribe, unsubscribe, isSubscribed };

  // Subscriptions are not counted per viewer, so on the way out we release the
  // stream only if we were the ones who started it. Dropping one the support
  // view had already claimed would leave that view with no data.
  const claimedHome = useRef(false);
  useEffect(() => {
    if (!deviceSerial) return;
    claimedHome.current = !ws.current.isSubscribed(deviceSerial, 'home');
    if (claimedHome.current) ws.current.subscribe(deviceSerial, 'home');
    return () => {
      if (claimedHome.current) ws.current.unsubscribe(deviceSerial, 'home');
      claimedHome.current = false;
    };
  }, [deviceSerial]);
  
  // Note: Offline handling is done by parent component (DeviceSubscriptions)
  // This component doesn't auto-close to prevent premature closing
  
  // Process live data updates (frequent)
  useEffect(() => {
    const topicData = getTopicData(deviceSerial, 'home');
    if (topicData?.data) {
      const { live, deviceInfo: info, activeProduct, activeBin: bin } = topicData.data;

      // Update live data
      if (live) {
        setLiveData(live);
      }

      // The product being dried rides alongside the readings rather than in them
      setProduct(activeProduct?.name || null);
      setProductIcon(activeProduct?.icon || null);

      // The bin being filled arrives beside the readings too, on a slower poll
      setActiveBin(bin || null);
      
      // Update device info
      if (info) {
        setDeviceInfo(info);
      }
    }
  }, [getTopicData, deviceSerial]);
  
  // Process chart data separately (less frequent)
  useEffect(() => {
    const topicData = getTopicData(deviceSerial, 'home');
    if (topicData?.data?.chart) {
      const { chart } = topicData.data;
      
      // Only update chart if data has actually changed
      if (chart && chart.data && Array.isArray(chart.data) && chart.data.length > 0) {
        // Create a unique key from the chart data to detect real changes
        const chartKey = chart.data.map(d => `${d.id}-${d.createdAt}`).join(',');
        
        // Only update if chart data has actually changed
        if (lastChartUpdate.current !== chartKey) {
          lastChartUpdate.current = chartKey;
          
          // Process chart data with correct field names
          const processedData = chart.data.map(item => ({
            time: format(new Date(item.created_at || item.createdAt), 'HH:mm'),
            timestamp: item.created_at || item.createdAt,
            // Moisture data (handle both old and new field names)
            inlet: parseFloat(item.inlet_moisture || item.inlet || 0),
            outlet: parseFloat(item.outlet_moisture || item.outlet || 0),
            target: parseFloat(item.moisture_target || item.target || 15), // Moisture setpoint
            // Rate data
            rate: parseFloat(item.discharge_rate || item.rate || 0),
            // Temperature
            apt: parseFloat(item.apt || 0),
            // Mode
            mode: item.mode,
            dryer_state: item.dryer_state
          }));
          
          // Sort by timestamp to ensure chronological order
          processedData.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
          
          // Store in ref and state
          chartDataRef.current = processedData;
          setChartData(processedData);
          
          console.log('[HomeDataDisplay] Chart data updated:', processedData.length, 'points');
        }
      }
    }
  }, [getTopicData, deviceSerial]);
  
  // Extract values from live data
  const getValue = (key) => {
    if (!liveData) return null;
    // If liveData is an array of items with name/value
    if (Array.isArray(liveData)) {
      const item = liveData.find(d => d.name === key);
      return item?.value;
    }
    // If liveData is an object with direct properties
    return liveData[key];
  };

  // The panel already converted these; we only put the right letter on them.
  const unit = tempUnit(getValue('temperature_unit'));

  // Automatic lock — same rule as the panel touchscreen: locked while the
  // dryer is not running or the model is not ready. Unknown (device offline)
  // locks nothing; the mode change itself fails and reports the error.
  const modelState = getValue('model_state');
  const dryerStateV2 = getValue('v2_dryer_state');
  const autoLocked =
    (dryerStateV2 != null && dryerStateV2 !== 'running') ||
    (modelState != null && modelState !== 'ready');
  const autoLockedReason =
    dryerStateV2 != null && dryerStateV2 !== 'running'
      ? 'Not ready — the dryer is not running'
      : modelState === 'waiting_to_initialize'
        ? 'Not ready — the model is initializing'
        : 'Not ready — the model is still learning';

  // Same state colours the support page uses for its dryer state banner.
  const dryerStateLabel = String(dryerStateV2 || 'standby')
    .replace(/_/g, ' ')
    .replace(/^./, (c) => c.toUpperCase());
  const dryerStateColor =
    dryerStateV2 === 'running' || dryerStateV2 === 'unloading' ? '#10b981'
    : dryerStateV2 === 'idle_run' || dryerStateV2 === 'preheat' ? '#f59e0b'
    : dryerStateV2 === 'primed' ? '#3b82f6'
    : '#6b7280';

  const mode = Number(getValue('mode_control'));

  // One set of dialogs for the whole dashboard. The cards at the top and the
  // System Controls box below both open these, the way the phone lets you
  // change a setpoint from either place.
  const controls = useDryerControls({
    mode,
    moistureSetpoint: getValue('moisture_setpoint'),
    rateSetpoint: getValue('discharge_rate_setpoint'),
    autoLocked,
    autoLockedReason,
    readOnly,
    onModeChange: handleModeChange,
    onSetpointChange: handleParameterUpdate,
  });

  const readOnlyHint = 'Read only — you cannot change this dryer';

  // Use memoized chart data to prevent re-renders
  const memoizedChartData = chartDataRef.current;

  return (
    <div className="space-y-8">
      {!liveData && (
        <div className="flex items-center gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <ArrowPathIcon className="w-5 h-5 text-blue-600 animate-spin" />
          <p className="text-sm text-blue-800">
            Waiting for live data from the dryer... If this stays empty for more
            than a few seconds, the dryer's cloud link may be down.
          </p>
        </div>
      )}

      {/* Metric Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
        <MetricCard
          title="Inlet"
          decimals={2}
          value={getValue('inlet_moisture')}
          unit="%"
          icon={Droplet}
          bgColor="#5cb85c"
          subValue={getValue('inlet_temperature')}
          subLabel="Temperature"
          subUnit={unit}
          subIcon={Thermometer}
        />

        <MetricCard
          title="Outlet"
          decimals={2}
          value={getValue('outlet_moisture')}
          unit="%"
          icon={Droplet}
          bgColor="#0961ad"
          subValue={getValue('outlet_temperature')}
          subLabel="Temperature"
          subUnit={unit}
          subIcon={Thermometer}
          setpoint={{
            value: getValue('moisture_setpoint'),
            disabled: controls.moistureDisabled,
            hint: readOnly ? readOnlyHint : 'The moisture setpoint is set in Automatic mode',
            onClick: controls.openMoisture,
          }}
        />

        <MetricCard
          title="Discharge Rate"
          decimals={2}
          value={getValue('discharge_rate')}
          icon={Gauge}
          bgColor="#f0ad4e"
          setpoint={{
            value: getValue('discharge_rate_setpoint'),
            disabled: controls.rateDisabled,
            hint: readOnly ? readOnlyHint : 'The rate setpoint is set in Manual mode',
            onClick: controls.openRate,
          }}
          modeControl={{
            mode,
            disabled: controls.modeDisabled,
            hint: readOnlyHint,
            onClick: controls.openMode,
          }}
        />
        
        <MetricCard
          title="Drying Temperature"
          value={getValue('drying_temperature')}
          unit={unit}
          icon={Thermometer}
          bgColor="#e74c3c"
        />
      </div>
      
      
      {/* Charts Section */}
      {memoizedChartData.length > 0 ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-6"
        >
          <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
            <MoistureChart data={memoizedChartData} />
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
            <DischargeRateChart data={memoizedChartData} />
          </div>
        </motion.div>
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gray-50 rounded-xl border-2 border-gray-200 p-8"
        >
          <div className="text-center">
            <ChartBarIcon className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-600">No chart data available yet</p>
            <p className="text-sm text-gray-500 mt-1">Charts will appear once data is received</p>
          </div>
        </motion.div>
      )}
      
      {/* Alarm Status Banner - if active */}
      {getValue('alarm_data') === 1 && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-red-50 border-2 border-red-300 rounded-xl p-6 shadow-xl"
        >
          <div className="flex items-center gap-4">
            <div className="p-3 bg-red-500 rounded-lg">
              <InformationCircleIcon className="w-8 h-8 text-white" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-red-900">Active Alarm</h3>
              <p className="text-base text-red-700 mt-1">
                {liveData?.find(d => d.name === 'alarm_data')?.message || 'System alarm active'}
              </p>
            </div>
          </div>
        </motion.div>
      )}
      
      {/* System Controls + System Information, the two boxes the phone shows */}
      <SystemControls
        mode={mode}
        moistureSetpoint={getValue('moisture_setpoint')}
        rateSetpoint={getValue('discharge_rate_setpoint')}
        dryerStateLabel={dryerStateLabel}
        dryerStateColor={dryerStateColor}
        productName={product || '—'}
        productIconName={productIcon}
        activeBin={activeBin}
        predictedMoisture={getValue('predicted_moisture')}
        suggestedRate={getValue('suggested_rate')}
        modelState={modelState}
        controls={controls}
      />

      {controls.dialogs}
    </div>
  );
};

export default HomeDataDisplay;